import {wasSystemApiErrorPresented} from '../../shared/errors/local-api-error';
import {AuthService} from '../../auth/auth.service';
import {BasketService} from '../../basket/basket.service';
import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {auditTime, catchError, filter, map, of, switchMap, takeUntil, timer, withLatestFrom} from 'rxjs';
import {ReportsService} from '../reports.service';
import {ReportsActions} from './reports.actions';
import {millisecondsUntilNextLocalDay, readLocalDateKey} from './reports-calendar';
import {selectEffectiveTimeZoneId} from './reports.selectors';

function resolveError(error: unknown): string {
    if (wasSystemApiErrorPresented(error)) return 'Unexpected error';
  return error instanceof Error && error.message.trim().length > 0 ? error.message : 'Unexpected error';
}

@Injectable()
export class ReportsEffects {
  readonly requestRefresh$ = createEffect(() => this.actions$.pipe(
    ofType(
      ReportsActions.applicationStarted,
      ReportsActions.applicationResumed,
      ReportsActions.effectiveTimeZoneChanged,
      ReportsActions.inventoryMutationCompleted,
      ReportsActions.localDayBoundaryReached,
    ),
    auditTime(25),
    map(() => ReportsActions.refreshRequested()),
  ));

  readonly refreshAfterBasketMutation$ = createEffect(() => this.basketService.changes$.pipe(
    withLatestFrom(this.authService.session$),
    filter(([, session]) => !!session),
    switchMap(() => timer(25).pipe(
      map(() => ReportsActions.refreshRequested()),
      takeUntil(this.actions$.pipe(ofType(ReportsActions.accountChanged))),
    )),
  ));

  readonly loadNearExpiry$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.refreshRequested),
    withLatestFrom(this.store.select(selectEffectiveTimeZoneId)),
    switchMap(([, timeZoneId]) => this.reportsService.getNearExpiryReport(timeZoneId).pipe(
      map((report) => ReportsActions.nearExpiryLoadSucceeded({
        items: report.items,
        localDate: readLocalDateKey(new Date(), timeZoneId),
      })),
      catchError((error: unknown) => of(ReportsActions.nearExpiryLoadFailed({error: resolveError(error)}))),
      takeUntil(this.actions$.pipe(ofType(ReportsActions.accountChanged))),
    )),
  ));

  readonly loadRecentChanges$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.refreshRequested),
    switchMap(() => this.reportsService.getRecentChangesReport().pipe(
      map((report) => ReportsActions.recentChangesLoadSucceeded({recentChanges: report.recentChanges, ...(report.recentEvents ? {recentEvents: report.recentEvents} : {})})),
      catchError((error: unknown) => of(ReportsActions.recentChangesLoadFailed({error: resolveError(error)}))),
      takeUntil(this.actions$.pipe(ofType(ReportsActions.accountChanged))),
    )),
  ));

  readonly scheduleNextLocalDay$ = createEffect(() => this.actions$.pipe(
    ofType(
      ReportsActions.applicationStarted,
      ReportsActions.applicationResumed,
      ReportsActions.effectiveTimeZoneChanged,
      ReportsActions.localDayBoundaryReached,
    ),
    withLatestFrom(this.store.select(selectEffectiveTimeZoneId)),
    switchMap(([, timeZoneId]) => timer(millisecondsUntilNextLocalDay(new Date(), timeZoneId)).pipe(
      map(() => ReportsActions.localDayBoundaryReached()),
    )),
  ));

  constructor(
    private readonly actions$: Actions,
    private readonly reportsService: ReportsService,
    private readonly store: Store,
    private readonly basketService: BasketService,
    private readonly authService: AuthService,
  ) {}
}
